import CircularLoader from "app/components/CircularLoader";
import { DeleteIcon } from "app/components/Icons";
import Clock from "app/components/Icons/Clock";
import CustomTooltip from "app/components/Tooltip";
import { useEffortEntry } from "app/containers/Features/components/EffortActual/useEffortEntry";
import { DatePicker } from "app/containers/Features/components/TimeTracker/DatePicker";
import { isoToTimeSlot } from "app/containers/Features/components/TimeTracker/helpers";
import TimePickerInput from "app/containers/Features/components/TimeTracker/TimePickerInput";
import { ticketDetailsLocales } from "app/containers/Features/containers/Ticket/locales";
import { IEffortActual } from "app/containers/Features/containers/Ticket/types";
import { ISelectedTicketDetails } from "app/containers/Features/containers/TimeSheet/types";
import { formatEffortShorthand } from "app/containers/Features/helper";
import clsx from "clsx";
import React from "react";
import { dateFormatter } from "utils/helpers";
import {
  generateEndTimeSlots,
  generateFullDayTimeSlots,
} from "utils/hooks/useTimeTracker";

const EffortEntryRow = ({
  entry,
  selectedTicketDetails,
}: {
  entry: IEffortActual;
  selectedTicketDetails: ISelectedTicketDetails;
}) => {
  const {
    isAssignee,
    localDate,
    localStartTime,
    localEndTime,
    selectedEntryId,
    timeSheetEntryDeleteLoading,
    handleDateChange,
    handleStartTimeChange,
    handleEndTimeChange,
    handleDelete,
  } = useEffortEntry(entry, selectedTicketDetails);

  return (
    <div
      className={clsx(
        "flex items-center justify-between gap-0.5 p-2 bg-gray-400 rounded",
      )}
    >
      <div className="flex items-center gap-1">
        <Clock size={24} />
        {isAssignee ? (
          <>
            <DatePicker
              value={localDate ? new Date(localDate) : null}
              onChange={handleDateChange}
              mode="edit"
            />
            <TimePickerInput
              mode="edit"
              slots={generateFullDayTimeSlots(new Date(localDate))}
              value={isoToTimeSlot(localStartTime)}
              onChange={handleStartTimeChange}
            />
            <span className="text-gray-300">-</span>
            <TimePickerInput
              mode="edit"
              slots={generateEndTimeSlots(new Date(localStartTime))}
              value={isoToTimeSlot(localEndTime)}
              onChange={handleEndTimeChange}
            />
          </>
        ) : (
          <p className="text-xs leading-none flex items-center gap-1.5 text-secondary">
            <span className="font-medium">
              {entry?.effort?.date
                ? dateFormatter(entry.effort.date, "onlyDate")
                : "No date"}
            </span>
            <span>{`${isoToTimeSlot(entry?.effort?.startTime)} - ${isoToTimeSlot(entry?.effort?.endTime)}`}</span>
          </p>
        )}
      </div>

      <div className="flex items-center gap-1.5 ml-2">
        <p className="text-xs text-nowrap font-medium text-secondary">
          {formatEffortShorthand(entry?.effort?.hours, entry?.effort?.minutes)}
        </p>
        <div
          className={clsx(
            "size-5 hover:bg-gray-300/30 rounded-sm cursor-pointer flex items-center justify-center",
            isAssignee
              ? "opacity-100"
              : "opacity-20 pointer-events-none cursor-not-allowed",
          )}
        >
          {timeSheetEntryDeleteLoading && selectedEntryId === entry._id ? (
            <CircularLoader size="sm" />
          ) : (
            <CustomTooltip
              text={ticketDetailsLocales.deleteEntry}
              position="bottom"
            >
              <DeleteIcon size={20} color="#EB5757" onClick={handleDelete} />
            </CustomTooltip>
          )}
        </div>
      </div>
    </div>
  );
};

export default EffortEntryRow;
